import { useState } from 'react';
import { 
  Box, 
  Typography, 
  Tooltip, 
  Chip, 
  Collapse, 
  IconButton, 
  useTheme,
  TextField,
  InputAdornment
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { tokens } from '../../theme';
import { CARD_BLOODLINES } from '../../constants/filterConstants';

/**
 * Bloodline Filter Component for Splinterlands cards
 * Shows all available bloodlines (sub_types) with toggleable selection and search functionality
 */
const BloodlineFilter = ({ selectedBloodlines, onBloodlineToggle }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [expanded, setExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter bloodlines based on search term
  const filteredBloodlines = CARD_BLOODLINES.filter(bloodline =>
    bloodline.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  return (
    <Box mb={2}>
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        onClick={() => setExpanded(!expanded)}
        sx={{ cursor: 'pointer', mb: 1 }}
      >
        <Typography variant="body2" color={colors.grey[300]}>
          Filter by Bloodline:
        </Typography>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      
      <Collapse in={expanded}>
        <Box 
          sx={{ 
            backgroundColor: colors.primary[400],
            borderRadius: 1,
            p: 1.5
          }}
        >
          {/* Search Field */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search bloodlines..."
            value={searchTerm}
            onChange={handleSearchChange}
            size="small"
            sx={{
              mb: 1.5,
              backgroundColor: colors.primary[500],
              borderRadius: 1,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: colors.grey[500],
                },
                '&:hover fieldset': {
                  borderColor: colors.grey[400],
                },
                '&.Mui-focused fieldset': {
                  borderColor: colors.greenAccent[500],
                },
              },
              '& .MuiInputBase-input': {
                color: colors.grey[100],
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.grey[300], fontSize: '18px' }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton 
                    onClick={handleClearSearch}
                    size="small"
                    sx={{ color: colors.grey[300] }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Bloodline Chips Grid */}
          <Box 
            sx={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
              gap: 0.8,
              maxHeight: '200px',
              overflowY: 'auto',
              overflowX: 'hidden'
            }}
          >
            {filteredBloodlines.map((bloodline) => {
              const isSelected = selectedBloodlines.includes(bloodline.id);
              return (
                <Tooltip key={bloodline.id} title={bloodline.name} arrow placement="top">
                  <Chip
                    label={bloodline.name}
                    size="small"
                    clickable
                    onClick={() => onBloodlineToggle(bloodline.id)}
                    sx={{
                      height: '28px',
                      backgroundColor: isSelected 
                        ? colors.greenAccent[500]
                        : colors.primary[600],
                      color: isSelected 
                        ? '#000000' 
                        : colors.grey[300],
                      fontWeight: isSelected ? 'bold' : 'normal',
                      border: '1px solid',
                      borderColor: isSelected 
                        ? colors.greenAccent[500]
                        : 'transparent',
                      '&:hover': {
                        backgroundColor: isSelected
                          ? colors.greenAccent[600]
                          : colors.primary[500]
                      },
                      '& .MuiChip-label': {
                        padding: '0 8px',
                        fontSize: '0.7rem'
                      }
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>

          {/* Show message if no bloodlines match search */}
          {filteredBloodlines.length === 0 && searchTerm && (
            <Typography 
              variant="body2" 
              color={colors.grey[400]} 
              sx={{ textAlign: 'center', mt: 1 }}
            >
              No bloodlines found matching "{searchTerm}"
            </Typography>
          )}

          {/* Show selected count */}
          {selectedBloodlines.length > 0 && (
            <Typography 
              variant="caption" 
              color={colors.greenAccent[400]} 
              sx={{ display: 'block', textAlign: 'center', mt: 1, fontWeight: 'bold' }}
            >
              {selectedBloodlines.length} bloodline{selectedBloodlines.length !== 1 ? 's' : ''} selected
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default BloodlineFilter;
